import { useAuth } from '../contexts/AuthContext'
import { Building2, ChevronDown, Search, Check, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import api from '../services/api'

const BuildingSwitcher = () => {
  const { isAdmin, availableBuildings, buildingsLoading, buildingsError, selectedBuildingId, selectedBuilding, selectedBuildingName, setSelectedBuilding, refreshBuildings } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)
  const debounceRef = useRef(null)
  // Every reply carries the generation it was issued under. Anything that abandons a
  // search bumps this, so a slow reply cannot land on top of a list the search box no
  // longer describes.
  const searchGenerationRef = useRef(0)

  // Abandon whatever search is in flight or still pending: the box no longer means it.
  const cancelPendingSearch = useCallback(() => {
    searchGenerationRef.current += 1
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSearching(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchQuery('')
        setSearchResults(null)
        cancelPendingSearch()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [cancelPendingSearch])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Server-side search for admins (debounced)
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null)
      setSearching(false)
      return
    }
    const generation = searchGenerationRef.current
    const isCurrent = () => generation === searchGenerationRef.current
    try {
      setSearching(true)
      const response = await api.get('/Building', {
        params: { searchString: query, pageSize: 20 }
      })
      if (!isCurrent()) return
      const items = response.data.items || []
      setSearchResults(items.map(b => ({
        buildingId: b.id || b.buildingId,
        name: b.name || b.userName,
        userName: b.userName,
        address: b.address
      })))
    } catch (e) {
      // Worth knowing about whether or not anyone is still waiting on it.
      console.error('Building search failed:', e)
      if (!isCurrent()) return
      setSearchResults([])
    } finally {
      // A superseded reply must not switch off the spinner for the search that replaced it.
      if (isCurrent()) setSearching(false)
    }
  }, [])

  const handleSearchChange = (value) => {
    setSearchQuery(value)
    cancelPendingSearch()

    if (isAdmin) {
      // Server-side search for admins
      if (!value.trim()) {
        setSearchResults(null)
        return
      }
      debounceRef.current = setTimeout(() => performSearch(value), 300)
    }
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  if (buildingsLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg">
        <Loader2 size={16} className="text-primary-600 animate-spin" />
        <span className="text-sm font-medium text-primary-700">Loading...</span>
      </div>
    )
  }

  // A failed load leaves the same empty list a building-less account does, but hiding the
  // header for it strands the user: the selected building id is remembered and still goes out
  // with every request, so they keep working inside a building nothing on screen names, and
  // the search box that could get them out is gone with the rest of the control. Offer the
  // retry instead. Ordered after the loading branch above so a retry in flight shows the
  // spinner rather than the error it is already busy clearing.
  if (availableBuildings.length === 0 && buildingsError) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
        <span className="text-sm font-medium text-amber-700">Buildings unavailable</span>
        <button
          onClick={() => refreshBuildings()}
          className="flex items-center gap-1 text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
        >
          <RefreshCw size={12} />
          Retry
        </button>
      </div>
    )
  }

  // Genuinely nothing to switch between: no control, exactly as before.
  if (availableBuildings.length === 0) return null

  const displayName = selectedBuildingName || 'Select Building'

  // Hand over the whole building, not just its id: a search result is not in
  // availableBuildings, so the id alone leaves the context with no way to name it.
  const handleSelect = (building) => {
    setSelectedBuilding(building)
    setIsOpen(false)
    setSearchQuery('')
    setSearchResults(null)
    cancelPendingSearch()
  }

  if (availableBuildings.length === 1 && !isAdmin) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg">
        <Building2 size={16} className="text-primary-600" />
        <span className="text-sm font-medium text-primary-700">{displayName}</span>
      </div>
    )
  }

  // For managers: client-side filter; for admins: use server results when searching
  const displayBuildings = isAdmin
    ? (searchResults !== null ? searchResults : availableBuildings)
    : (searchQuery
        ? availableBuildings.filter(b =>
            b.name?.toLowerCase().includes(searchQuery.toLowerCase()))
        : availableBuildings)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors max-w-[280px]"
      >
        <Building2 size={16} className="text-primary-600 flex-shrink-0" />
        <div className="flex flex-col items-start min-w-0">
          <span className="text-sm font-medium text-primary-700 truncate leading-tight">{displayName}</span>
          {selectedBuilding?.address && (
            <span className="text-[10px] text-primary-500/70 truncate leading-tight max-w-[200px]">{selectedBuilding.address}</span>
          )}
        </div>
        <ChevronDown size={14} className={`text-primary-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-96 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
          {/* Search input — always shown for admins, shown for managers with >5 buildings */}
          {(isAdmin || availableBuildings.length > 5) && (
            <div className="px-3 py-2 border-b border-slate-100">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={isAdmin ? "Search by name, username, or address..." : "Search buildings..."}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {searching && (
                  <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
                )}
              </div>
            </div>
          )}
          <div className="max-h-80 overflow-y-auto">
            {searching && searchResults === null ? (
              <div className="px-4 py-6 text-sm text-slate-500 text-center">
                <Loader2 size={20} className="animate-spin mx-auto mb-2 text-slate-400" />
                Searching...
              </div>
            ) : displayBuildings.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500 text-center">
                {searchQuery ? 'No buildings found' : 'No buildings available'}
              </div>
            ) : (
              displayBuildings.map((building) => {
                const isSelected = building.buildingId === selectedBuildingId
                return (
                  <button
                    key={building.buildingId}
                    onClick={() => handleSelect(building)}
                    className={`w-full text-left px-4 py-2.5 flex items-start justify-between hover:bg-slate-50 transition-colors ${
                      isSelected
                        ? 'bg-primary-50'
                        : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm truncate ${isSelected ? 'text-primary-600 font-semibold' : 'text-slate-900 font-medium'}`}>
                        {building.name}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {building.userName && (
                          <span className="text-xs text-slate-400">@{building.userName}</span>
                        )}
                        {building.address && (
                          <span className="text-xs text-slate-400 truncate">{building.address}</span>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check size={14} className="text-primary-600 flex-shrink-0 ml-2 mt-0.5" />}
                  </button>
                )
              })
            )}
          </div>
          {isAdmin && searchQuery && searchResults && (
            <div className="px-3 py-1.5 border-t border-slate-100 text-xs text-slate-400 text-center">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default BuildingSwitcher
